import PageHero from "@/components/PageHero";
import { useState } from "react";
import { Mail, MapPin, Phone, MessageCircle, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useOutletContext } from "react-router-dom";

export default function Connect() {
  const ctx = useOutletContext();
  const s = ctx?.settings || {};
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Name and phone are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/supporters", {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        message: form.message,
      });
      toast.success("Message received. We'll be in touch.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message.");
    } finally {
      setBusy(false);
    }
  };

  const wa = (s.whatsapp || "+919555885651").replace(/[^\d]/g, "");

  return (
    <div data-testid="connect-page">
      <PageHero eyebrow="Connect" title="Talk to Team Sankalp" subtitle="Drop a message, share a suggestion, or volunteer for the campaign." />
      <section className="section-y">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-6">
            <div className="border border-[hsl(var(--border-c))] p-6 flex items-start gap-4">
              <Phone className="text-[hsl(var(--accent))]" />
              <div>
                <div className="text-xs uppercase tracking-widest text-[hsl(var(--fg-muted))]">Phone</div>
                <div className="font-display text-2xl text-[hsl(var(--primary))]">{s.phone || "+91 95558 85651"}</div>
              </div>
            </div>
            <div className="border border-[hsl(var(--border-c))] p-6 flex items-start gap-4">
              <Mail className="text-[hsl(var(--accent))]" />
              <div>
                <div className="text-xs uppercase tracking-widest text-[hsl(var(--fg-muted))]">Email</div>
                <div className="font-display text-2xl text-[hsl(var(--primary))] break-all">{s.email || "contact.abhishekgarg@gmail.com"}</div>
              </div>
            </div>
            <div className="border border-[hsl(var(--border-c))] p-6 flex items-start gap-4">
              <MapPin className="text-[hsl(var(--accent))]" />
              <div>
                <div className="text-xs uppercase tracking-widest text-[hsl(var(--fg-muted))]">Address</div>
                <div className="text-lg text-[hsl(var(--primary))]">{s.address || "New Delhi, India"}</div>
              </div>
            </div>
            <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" data-testid="connect-whatsapp-btn"
              className="bg-[#25D366] text-white p-6 flex items-center gap-4 hover:opacity-90 transition-opacity">
              <MessageCircle />
              <div>
                <div className="text-xs uppercase tracking-widest opacity-80">WhatsApp Team Sankalp</div>
                <div className="font-display text-xl">{s.whatsapp || "+91 95558 85651"}</div>
              </div>
            </a>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="bg-white border-2 border-[hsl(var(--primary))] p-8 space-y-5" data-testid="connect-form">
              <h2 className="font-display text-3xl text-[hsl(var(--primary))]">Send a message</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="c-name" className="uppercase text-[10px] tracking-[0.2em]">Name *</Label>
                  <Input id="c-name" data-testid="connect-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div>
                  <Label htmlFor="c-phone" className="uppercase text-[10px] tracking-[0.2em]">Phone *</Label>
                  <Input id="c-phone" data-testid="connect-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
                </div>
              </div>
              <div>
                <Label htmlFor="c-email" className="uppercase text-[10px] tracking-[0.2em]">Email</Label>
                <Input id="c-email" type="email" data-testid="connect-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="c-msg" className="uppercase text-[10px] tracking-[0.2em]">Message *</Label>
                <Textarea id="c-msg" data-testid="connect-message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required />
              </div>
              <Button type="submit" disabled={busy} data-testid="connect-submit"
                className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))] text-white uppercase tracking-widest rounded-none">
                {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={16} className="mr-2" />}
                Send Message
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
